import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { NgxPermissionsService } from 'ngx-permissions';
import { _, TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import {
  LdActionPlace,
  LdActionType,
  LdCoordinatorDispatch,
} from '@app/main/leak-detection/services/leak-detection-coordinator/leak-detection-coordinator.actions';
import { contextMenuTodo } from '@app/main/main-content/context-menu/utils/context-menu-todo';
import { ICorrelation } from '@app/api/interfaces/correlation.interface';
import { ILoggerMeasurement } from '@app/api/interfaces/logger-measurement.interface';
import { IMainTableCellContext } from '@app/main/main-content/main-table/interfaces/main-table-cell-context.interface';
import { ContextMenuCopyService } from '@app/main/main-content/context-menu/services/context-menu-copy.service';
import { LdContextMenuEventsService } from '@app/main/leak-detection/context-menus/services/ld-context-menu-events.service';
import { LeakDetectionSoundSignalService } from '@app/main/leak-detection/services/leak-detection-sound-signal/leak-detection-sound-signal.service';
import { PermissionName } from '@app/core/permissions/enums/permission-name.enum';
import { selectAllLoggerMeasurements } from '@app/main/main-screen/store/data/logger-measurements/logger-measurements.selectors';
import { selectPipesLoaded } from '@app/main/main-screen/store/data/pipes/pipes.selectors';

@Injectable({ providedIn: 'root' })
export class LdContextMenuCorrelationService {
  private readonly translateService = inject(TranslateService);
  private readonly ngxPermissionsService = inject(NgxPermissionsService);
  private readonly store = inject(Store);
  private readonly contextMenuCopy = inject(ContextMenuCopyService);
  private readonly contextMenuEvents = inject(LdContextMenuEventsService);
  private readonly soundSignalService = inject(LeakDetectionSoundSignalService);

  private readonly loggerMeasurements = this.store.selectSignal(selectAllLoggerMeasurements);
  private readonly pipesLoaded = this.store.selectSignal(selectPipesLoaded);

  public getMapItems(correlation: ICorrelation, dispatch: LdCoordinatorDispatch): MenuItem[] {
    if (!this.canAccessCorrelation()) return [];

    return this.getCommonItems(correlation, LdActionPlace.Map, dispatch);
  }

  public getTableItems(
    correlation: ICorrelation,
    cell: IMainTableCellContext | undefined,
    dispatch: LdCoordinatorDispatch
  ): MenuItem[] {
    if (!this.canAccessCorrelation()) return [];

    return [
      ...this.contextMenuCopy.getCopyCellItems(cell),
      ...this.getCommonItems(correlation, LdActionPlace.TabsPanel, dispatch),
    ];
  }

  private canAccessCorrelation(): boolean {
    return !!this.ngxPermissionsService.getPermission(PermissionName.LeakDetection_Read_CorrelationContextMenu);
  }

  private getCommonItems(correlation: ICorrelation, place: LdActionPlace, dispatch: LdCoordinatorDispatch): MenuItem[] {
    const canEditMap = !!this.ngxPermissionsService.getPermission(PermissionName.LeakDetection_Write_MapEditing);

    const detailsGroup: MenuItem[] = [
      this.getCorrelationDetailsItem(correlation, place, dispatch),
      this.getDownloadSoundFileItem(correlation),
      this.getManualCorrelationItem(correlation),
    ];

    const mapEditingGroup: MenuItem[] = canEditMap
      ? [
          this.getCreatePipeItem(correlation),
          this.getEditPipeItem(correlation),
          this.contextMenuEvents.getCreateEventMenuItem(correlation.coordinates, place, dispatch),
        ]
      : [];

    const streetViewGroup: MenuItem[] = [this.getShowInStreetViewItem(correlation, place, dispatch)];

    return this.joinGroups([detailsGroup, mapEditingGroup, streetViewGroup]);
  }

  private getCorrelationDetailsItem(
    correlation: ICorrelation,
    place: LdActionPlace,
    dispatch: LdCoordinatorDispatch
  ): MenuItem {
    return {
      label: this.translateService.instant(_(`@@leak-detection-context-menu-correlation-details__label`)),
      icon: 'icons/icon-chart.svg',
      command: () => dispatch({ type: LdActionType.CorrelationDbClick, place, item: correlation }),
    };
  }

  private getDownloadSoundFileItem(correlation: ICorrelation): MenuItem {
    const measurement1 = this.findLoggerSoundMeasurement(
      correlation.logger1DeploymentId,
      correlation.soundSignalStartAt
    );
    const measurement2 = this.findLoggerSoundMeasurement(
      correlation.logger2DeploymentId,
      correlation.soundSignalStartAt
    );
    const hasFile1 = !!measurement1?.soundSignalUrl;
    const hasFile2 = !!measurement2?.soundSignalUrl;

    const isDeveloper = !!this.ngxPermissionsService.getPermission(
      PermissionName.LeakDetection_Read_CorrelationSoundFileChannels
    );

    if (isDeveloper) {
      return {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-download-sound-file__label`)),
        icon: 'icons/icon-noise.svg',
        disabled: !hasFile1 && !hasFile2,
        hideSubmenuArrow: true,
        items: [
          {
            label: this.translateService.instant(_(`@@leak-detection-context-menu-download-sound-file-logger1__label`)),
            icon: 'icons/icon-download.svg',
            disabled: !hasFile1,
            command: () => this.soundSignalService.downloadByMeasurement(measurement1),
          },
          {
            label: this.translateService.instant(_(`@@leak-detection-context-menu-download-sound-file-logger2__label`)),
            icon: 'icons/icon-download.svg',
            disabled: !hasFile2,
            command: () => this.soundSignalService.downloadByMeasurement(measurement2),
          },
        ],
      };
    }

    return {
      label: this.translateService.instant(_(`@@leak-detection-context-menu-download-sound-file__label`)),
      icon: 'icons/icon-noise.svg',
      disabled: !hasFile1 && !hasFile2,
      command: () => {
        this.soundSignalService.downloadByMeasurement(measurement1);
        this.soundSignalService.downloadByMeasurement(measurement2);
      },
    };
  }

  private getManualCorrelationItem(correlation: ICorrelation): MenuItem {
    return {
      label: this.translateService.instant(_(`@@leak-detection-context-menu-manual-correlation__label`)),
      icon: 'icons/icon-drop.svg',
      // manual correlation needs every project pipe loaded to place the result correctly
      disabled: !this.pipesLoaded(),
      command: () => contextMenuTodo('Manual correlation', correlation),
    };
  }

  private getCreatePipeItem(correlation: ICorrelation): MenuItem {
    return {
      label: this.translateService.instant(_(`@@leak-detection-context-menu-create-pipe__label`)),
      icon: 'icons/icon-create-pipe.svg',
      command: () => contextMenuTodo('Create pipe', correlation),
    };
  }

  private getEditPipeItem(correlation: ICorrelation): MenuItem {
    return {
      label: this.translateService.instant(_(`@@leak-detection-context-menu-edit-pipe__label`)),
      icon: 'icons/icon-edit.svg',
      command: () => contextMenuTodo('Edit pipe', correlation),
    };
  }

  private getShowInStreetViewItem(
    correlation: ICorrelation,
    place: LdActionPlace,
    dispatch: LdCoordinatorDispatch
  ): MenuItem {
    return {
      label: this.translateService.instant(_(`@@leak-detection-context-menu-show-in-street-view__label`)),
      icon: 'icons/icon-show-on-street.svg',
      iconStyle: { 'original-icon': true },
      command: () => dispatch({ type: LdActionType.ShowInStreetView, place, position: correlation.coordinates }),
    };
  }

  private findLoggerSoundMeasurement(
    deploymentId: string,
    soundSignalStartAt: string | undefined
  ): ILoggerMeasurement | undefined {
    if (!soundSignalStartAt) return undefined;

    return this.loggerMeasurements().find(
      measurement => measurement.deploymentId === deploymentId && measurement.soundSignalStartAt === soundSignalStartAt
    );
  }

  private joinGroups(groups: MenuItem[][]): MenuItem[] {
    return groups
      .filter(group => group.length > 0)
      .flatMap((group, index) => (index === 0 ? group : [{ separator: true }, ...group]));
  }
}
